<template>
  <div class="shop-theme-customizer">
    <div class="row">
      <div class="col-lg-7 mb-4 mb-lg-0">
        <!-- Preset themes -->
        <div class="theme-section mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="theme-label mb-0">
              Tema Preset
              <i class="fas fa-info-circle text-muted ml-1" title="Klik preset untuk langsung menerapkan kombinasi warna"></i>
            </label>
            <button type="button" class="btn btn-link btn-sm p-0 text-muted" @click="resetTheme">
              <i class="fas fa-undo"></i> Reset Default
            </button>
          </div>
          <div class="preset-grid">
            <button
              v-for="preset in presets"
              :key="preset.id"
              type="button"
              class="preset-card"
              :class="{ active: local.theme_preset === preset.id }"
              @click="applyPreset(preset)"
              :title="preset.name"
            >
              <span class="preset-swatches">
                <span :style="{ background: preset.primary }"></span>
                <span :style="{ background: preset.accent }"></span>
              </span>
              <span class="preset-emoji">{{ preset.emoji }}</span>
              <span class="preset-name">{{ preset.name }}</span>
            </button>
          </div>
        </div>

        <!-- Color pickers -->
        <div class="row mb-4">
          <div class="col-md-6 mb-3 mb-md-0">
            <label class="theme-label">
              Warna Utama (Brand)
              <i class="fas fa-info-circle text-muted ml-1" title="Warna header dan elemen utama toko"></i>
            </label>
            <div class="color-input-row">
              <input
                v-model="local.theme_color"
                type="color"
                class="color-picker-native"
                @input="onCustomColor"
              >
              <input
                v-model="local.theme_color"
                type="text"
                class="form-control color-hex-input"
                maxlength="7"
                placeholder="#1976d2"
                @input="onCustomColor"
              >
            </div>
          </div>
          <div class="col-md-6">
            <label class="theme-label">
              Warna Aksen
              <small class="text-muted">(opsional)</small>
              <i class="fas fa-info-circle text-muted ml-1" title="Untuk badge, highlight, dan tombol sekunder"></i>
            </label>
            <div class="color-input-row">
              <input
                v-model="accentDisplay"
                type="color"
                class="color-picker-native"
                @input="onAccentChange"
              >
              <input
                v-model="accentDisplay"
                type="text"
                class="form-control color-hex-input"
                maxlength="7"
                placeholder="Auto dari utama"
                @input="onAccentChange"
              >
            </div>
            <small class="text-muted">Kosongkan untuk otomatis dari warna utama</small>
          </div>
        </div>

        <!-- Advanced options -->
        <div class="theme-section">
          <label class="theme-label mb-3">Opsi Lanjutan</label>
          <div class="row">
            <div class="col-md-6 mb-3">
              <span class="d-block small text-muted mb-2">Gaya Header</span>
              <div class="btn-group btn-group-toggle w-100" role="group">
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="local.theme_header_style === 'gradient' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="setHeaderStyle('gradient')"
                >
                  Gradient
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="local.theme_header_style === 'solid' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="setHeaderStyle('solid')"
                >
                  Solid
                </button>
              </div>
            </div>
            <div class="col-md-6 mb-3">
              <span class="d-block small text-muted mb-2">Gaya Tombol</span>
              <div class="btn-group btn-group-toggle w-100" role="group">
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="local.theme_button_style === 'rounded' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="setButtonStyle('rounded')"
                >
                  Rounded
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="local.theme_button_style === 'pill' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="setButtonStyle('pill')"
                >
                  Pill
                </button>
              </div>
            </div>
          </div>
          <div class="auto-contrast-note">
            <i class="fas fa-magic text-primary"></i>
            Teks header otomatis menyesuaikan kontras
            <span class="contrast-badge" :style="{ background: previewVars.primary, color: previewVars.headerText }">
              {{ previewVars.headerText === '#ffffff' ? 'Putih' : 'Gelap' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Live preview -->
      <div class="col-lg-5">
        <div class="preview-panel">
          <div class="preview-label">
            <i class="fas fa-eye"></i> Preview Langsung
          </div>
          <div class="preview-mockup" :style="previewCssVars">
            <div class="preview-header" :style="{ background: previewVars.headerBackground, color: previewVars.headerText }">
              <div class="preview-logo"></div>
              <div>
                <div class="preview-shop-name">Nama Toko Anda</div>
                <small class="preview-shop-desc">Toko online terpercaya</small>
              </div>
            </div>
            <div class="preview-body">
              <div class="preview-card">
                <div class="preview-card-img"></div>
                <div class="preview-card-title">Produk Contoh</div>
                <div class="preview-card-price">Rp 99.000</div>
                <span class="preview-badge">Terlaris</span>
                <button type="button" class="preview-btn" :style="{ borderRadius: previewVars.btnRadius }">
                  Beli Sekarang
                </button>
              </div>
              <div class="preview-progress-wrap">
                <small>Progress donasi</small>
                <div class="preview-progress">
                  <div class="preview-progress-bar"></div>
                </div>
              </div>
            </div>
          </div>
          <small class="text-muted d-block mt-2 text-center">
            Perubahan tampil langsung — klik Simpan Settings untuk menerapkan ke toko
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  THEME_PRESETS,
  DEFAULT_THEME,
  buildShopThemeVars,
  normalizeHex,
} from '@/components/shop_public/shop_public_utils';

export default {
  name: 'ShopThemeCustomizer',
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      presets: THEME_PRESETS,
      local: { ...DEFAULT_THEME },
    };
  },
  computed: {
    previewVars() {
      return buildShopThemeVars(this.local);
    },
    previewCssVars() {
      const v = this.previewVars;
      return {
        '--shop-primary': v.primary,
        '--shop-primary-dark': v.primaryDark,
        '--shop-accent': v.accent,
        '--shop-header-text': v.headerText,
        '--shop-btn-radius': v.btnRadius,
      };
    },
    accentDisplay: {
      get() {
        return this.local.theme_accent_color || this.previewVars.accent;
      },
      set(val) {
        this.local.theme_accent_color = val;
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(v) {
        if (!v) return;
        this.local = {
          theme_color: v.theme_color || DEFAULT_THEME.theme_color,
          theme_accent_color: v.theme_accent_color || '',
          theme_preset: v.theme_preset || '',
          theme_header_style: v.theme_header_style || 'gradient',
          theme_button_style: v.theme_button_style || 'rounded',
        };
      },
    },
    local: {
      deep: true,
      handler(v) {
        this.$emit('input', { ...v });
      },
    },
  },
  methods: {
    applyPreset(preset) {
      this.local = {
        theme_color: preset.primary,
        theme_accent_color: preset.accent,
        theme_preset: preset.id,
        theme_header_style: this.local.theme_header_style,
        theme_button_style: this.local.theme_button_style,
      };
    },
    onCustomColor() {
      this.local.theme_color = normalizeHex(this.local.theme_color);
      this.local.theme_preset = '';
    },
    onAccentChange() {
      const val = this.accentDisplay;
      if (!val || val === this.previewVars.accent) {
        this.local.theme_accent_color = '';
      } else {
        this.local.theme_accent_color = normalizeHex(val);
      }
      this.local.theme_preset = '';
    },
    setHeaderStyle(style) {
      this.local.theme_header_style = style;
    },
    setButtonStyle(style) {
      this.local.theme_button_style = style;
    },
    resetTheme() {
      this.local = {
        theme_color: DEFAULT_THEME.theme_color,
        theme_accent_color: '',
        theme_preset: '',
        theme_header_style: 'gradient',
        theme_button_style: 'rounded',
      };
    },
  },
};
</script>

<style scoped>
.shop-theme-customizer {
  --preview-radius: 12px;
}
.theme-label {
  font-weight: 600;
  font-size: 14px;
  display: block;
  margin-bottom: 8px;
}
.theme-section {
  background: #f8f9fb;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #eef0f4;
}
.preset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 10px;
}
.preset-card {
  border: 2px solid #e8eaef;
  border-radius: 12px;
  background: #fff;
  padding: 10px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}
.preset-card:hover {
  border-color: #90caf9;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.12);
}
.preset-card.active {
  border-color: #1976d2;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
}
.preset-swatches {
  display: flex;
  gap: 4px;
  justify-content: center;
  margin-bottom: 6px;
}
.preset-swatches span {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.8);
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.preset-emoji {
  display: block;
  font-size: 16px;
  line-height: 1;
}
.preset-name {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #444;
  margin-top: 4px;
  line-height: 1.2;
}
.color-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.color-picker-native {
  width: 48px;
  height: 48px;
  padding: 2px;
  border: 2px solid #dee2e6;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;
  background: #fff;
}
.color-hex-input {
  font-family: monospace;
  font-size: 14px;
  text-transform: lowercase;
}
.auto-contrast-note {
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.contrast-badge {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}
.preview-panel {
  position: sticky;
  top: 16px;
}
.preview-label {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}
.preview-mockup {
  border-radius: var(--preview-radius);
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  background: #f4f6f8;
}
.preview-header {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background 0.3s ease, color 0.3s ease;
}
.preview-logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,0.25);
  flex-shrink: 0;
}
.preview-shop-name {
  font-weight: 700;
  font-size: 15px;
}
.preview-shop-desc {
  opacity: 0.85;
}
.preview-body {
  padding: 16px;
}
.preview-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  transition: all 0.3s ease;
}
.preview-card-img {
  height: 80px;
  border-radius: 8px;
  background: linear-gradient(135deg, #eef1f5, #dde3ea);
  margin-bottom: 10px;
}
.preview-card-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
}
.preview-card-price {
  color: var(--shop-primary, #1976d2);
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 8px;
  transition: color 0.3s ease;
}
.preview-badge {
  display: inline-block;
  background: var(--shop-accent, #42a5f5);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  margin-bottom: 10px;
  transition: background 0.3s ease;
}
.preview-btn {
  display: block;
  width: 100%;
  border: none;
  background: var(--shop-primary, #1976d2);
  color: #fff;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: default;
  transition: background 0.3s ease, border-radius 0.3s ease;
}
.preview-progress-wrap {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
}
.preview-progress-wrap small {
  color: #888;
  display: block;
  margin-bottom: 6px;
}
.preview-progress {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}
.preview-progress-bar {
  width: 65%;
  height: 100%;
  background: var(--shop-primary, #1976d2);
  border-radius: 4px;
  transition: background 0.3s ease;
}
@media (max-width: 991px) {
  .preview-panel {
    position: static;
  }
  .preset-grid {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  }
}
</style>
